import { Link } from "react-router-dom";
import { LegalLayout, Section, Table, List } from "./LegalLayout";
import { COMPANY, displayLegalName, formatAddress, privacyContact } from "@/config/company";

/**
 * Informativa privacy ex artt. 13-14 GDPR.
 *
 * Copre due situazioni diverse che è importante non confondere:
 *  - AURA come TITOLARE, per i dati di chi visita il sito o chiede una demo;
 *  - AURA come RESPONSABILE, per i dati delle clienti che i centri estetici
 *    caricano nella piattaforma (lì il titolare è il centro).
 */
export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Informativa sulla privacy"
      subtitle="Come trattiamo i dati personali di chi visita questo sito, richiede una demo o usa la piattaforma AURA."
    >
      <Section n={1} title="Titolare del trattamento">
        <p>
          Il titolare del trattamento è <strong>{displayLegalName()}</strong>
          {formatAddress() ? `, con sede in ${formatAddress()}` : ""}
          {COMPANY.vat ? `, P.IVA ${COMPANY.vat}` : ""}
          {COMPANY.rea ? `, REA ${COMPANY.rea}` : ""}. {COMPANY.brand} è un software di
          proprietà di {COMPANY.owner}; questo sito ne cura la presentazione e la
          commercializzazione.
        </p>
        <p>
          Per qualsiasi questione relativa ai dati personali, incluso l'esercizio dei
          diritti descritti al punto 9, puoi scrivere a{" "}
          <a href={`mailto:${privacyContact()}`} className="underline underline-offset-4">
            {privacyContact()}
          </a>
          {COMPANY.pec ? ` oppure via PEC a ${COMPANY.pec}` : ""}.
        </p>
        {COMPANY.dpo ? (
          <p>
            Responsabile della protezione dei dati (DPO): {COMPANY.dpo.name} —{" "}
            {COMPANY.dpo.email}.
          </p>
        ) : (
          <p>
            Non abbiamo nominato un Responsabile della protezione dei dati (DPO) perché
            non ricorrono le condizioni dell'art. 37 GDPR. Le richieste vanno indirizzate
            direttamente al titolare.
          </p>
        )}
      </Section>

      <Section n={2} title="Due ruoli diversi, da non confondere">
        <p>
          <strong>Quando siamo titolari.</strong> Per i dati di chi naviga su questo sito,
          compila il form di richiesta demo, ci scrive o gestisce un account di
          amministrazione, le decisioni su finalità e modalità del trattamento sono nostre.
        </p>
        <p>
          <strong>Quando siamo responsabili.</strong> I dati delle clienti finali inseriti
          in AURA dai centri estetici (anagrafiche, appuntamenti, trattamenti, punti
          fedeltà, eventuali fotografie) appartengono al centro, che è il titolare del
          trattamento. Noi li trattiamo esclusivamente su sua istruzione, nei limiti
          dell'accordo di nomina a responsabile ex art. 28 GDPR sottoscritto con ciascun
          centro. Se sei cliente di un centro estetico e vuoi esercitare i tuoi diritti,
          rivolgiti al centro: noi lo assisteremo tecnicamente.
        </p>
      </Section>

      <Section n={3} title="Quali dati trattiamo e perché">
        <Table
          head={["Chi", "Dati", "Finalità", "Base giuridica", "Conservazione"]}
          rows={[
            [
              "Visitatori del sito",
              "Indirizzo IP, data e ora della richiesta, pagina richiesta, user agent (log tecnici del server e della CDN)",
              "Erogare il sito, garantirne la sicurezza e diagnosticare malfunzionamenti",
              "Legittimo interesse alla sicurezza e al corretto funzionamento del servizio (art. 6.1.f)",
              "Massimo 30 giorni, poi cancellazione automatica",
            ],
            [
              "Chi richiede una demo",
              "Nome e cognome, email, telefono, nome del salone, piano di interesse, messaggio libero",
              "Rispondere alla richiesta, ricontattarti e mostrarti il prodotto",
              "Misure precontrattuali richieste dall'interessato (art. 6.1.b)",
              "24 mesi dall'ultimo contatto, salvo che il rapporto prosegua",
            ],
            [
              "Clienti dei centri (utenti registrati online)",
              "Nome, email, telefono, password cifrata, prenotazioni, storico trattamenti, punti fedeltà, credito wallet",
              "Permettere prenotazione online, area riservata e programma fedeltà del centro",
              "Contratto con il centro (art. 6.1.b). Titolare è il centro: noi siamo responsabili",
              "Per la durata del rapporto con il centro; alla chiusura dell'account del centro tutti i dati sono eliminati",
            ],
            [
              "Titolari e staff dei centri",
              "Nome, email, ruolo, credenziali di accesso, log di accesso",
              "Fornire il servizio, autenticazione, assistenza, fatturazione",
              "Esecuzione del contratto (art. 6.1.b) e obblighi di legge fiscali (art. 6.1.c)",
              "Durata del contratto; i documenti fiscali 10 anni (art. 2220 c.c.)",
            ],
            [
              "Chi riceve comunicazioni promozionali",
              "Email, nome",
              "Invio di newsletter e aggiornamenti sul prodotto",
              "Consenso libero e revocabile (art. 6.1.a e art. 130 D.Lgs 196/2003)",
              "Fino a revoca del consenso o disiscrizione",
            ],
          ]}
        />
        <p>
          Non svolgiamo processi decisionali automatizzati che producano effetti giuridici
          o incidano in modo analogamente significativo sulle persone (art. 22 GDPR).
        </p>
      </Section>

      <Section n={4} title="Categorie particolari di dati">
        <p>
          Alcune funzioni della piattaforma possono comportare il trattamento di dati che
          rivelano lo stato di salute o che derivano da immagini del volto (ad esempio
          l'analisi visiva della pelle o le note su allergie e controindicazioni). Sono
          <strong> categorie particolari ai sensi dell'art. 9 GDPR</strong>.
        </p>
        <p>
          Questi dati vengono trattati esclusivamente quando il centro estetico attiva la
          relativa funzione, e solo previo <strong>consenso esplicito e separato</strong>{" "}
          della persona interessata, raccolto dal centro tramite l'apposito modulo. Il
          consenso è sempre revocabile e la revoca comporta la cancellazione delle immagini
          e delle analisi collegate.
        </p>
        <p>
          Le funzioni di intelligenza artificiale del gestionale del centro (analisi della
          pelle, prodotti consigliati in base alle allergie, tono delle note, assistente
          vocale) possono inviare questi dati a <strong>Google (Gemini)</strong> e, per la voce,
          a <strong>ElevenLabs</strong>: l'invio parte dal computer del centro, con la chiave
          del centro, è disattivato di default e avviene solo con il consenso esplicito della
          cliente all'analisi con IA, senza nome e contatti. Questi fornitori sono scelti e
          nominati dal centro, possono trattare i dati anche fuori dall'UE con le garanzie
          previste (Data Privacy Framework o clausole contrattuali tipo).
        </p>
        <p>
          Le fotografie caricate per l'analisi della pelle non vengono usate per addestrare
          modelli di intelligenza artificiale né condivise con altri centri. Trovi il
          dettaglio nella{" "}
          <Link to="/intelligenza-artificiale" className="underline underline-offset-4">
            pagina sull'uso dell'intelligenza artificiale
          </Link>
          .
        </p>
      </Section>

      <Section n={5} title="Chi tratta i dati per nostro conto">
        <p>
          Ci avvaliamo di fornitori selezionati, nominati responsabili del trattamento ex
          art. 28 GDPR. L'elenco aggiornato è il seguente:
        </p>
        <Table
          head={["Fornitore", "Servizio", "Dove sono i dati"]}
          rows={[
            ["Cloudflare, Inc.", "Registrazione dominio, DNS, CDN e hosting delle pagine statiche", "Rete globale, con nodi nell'UE"],
            ["Render Services, Inc.", "Hosting dell'applicazione server", "Francoforte, Germania (UE)"],
            ["MongoDB, Inc. (Atlas)", "Database della piattaforma", "Francoforte, Germania (UE) — infrastruttura AWS"],
            ["Sendinblue SAS (Brevo)", "Invio delle email transazionali (conferme, reset password, notifiche)", "Unione Europea"],
          ]}
        />
        <p>
          I dati possono inoltre essere comunicati a consulenti contabili e legali, e alle
          autorità quando previsto da un obbligo di legge. Non vendiamo né cediamo dati
          personali a terzi per finalità commerciali.
        </p>
      </Section>

      <Section n={6} title="Trasferimenti fuori dall'Unione Europea">
        <p>
          I dati della piattaforma sono conservati su server situati nell'Unione Europea
          (Francoforte). Alcuni fornitori sono società statunitensi che possono accedere ai
          dati per manutenzione: in questi casi il trasferimento è regolato dalle clausole
          contrattuali tipo approvate dalla Commissione europea (decisione 2021/914) e,
          ove applicabile, dalla certificazione EU-U.S. Data Privacy Framework.
        </p>
        <p>
          Puoi richiederci copia delle garanzie adottate scrivendo a {privacyContact()}.
        </p>
      </Section>

      <Section n={7} title="Cookie e strumenti di tracciamento">
        <p>
          Questo sito utilizza <strong>esclusivamente cookie tecnici</strong>, necessari al
          funzionamento e all'autenticazione. Non usiamo cookie di profilazione, non
          facciamo pubblicità comportamentale e non registriamo le sessioni di navigazione.
          Il dettaglio completo è nella{" "}
          <Link to="/cookie-policy" className="underline underline-offset-4">
            Cookie Policy
          </Link>
          .
        </p>
      </Section>

      <Section n={8} title="Sicurezza">
        <p>Adottiamo misure tecniche e organizzative adeguate al rischio, tra cui:</p>
        <List
          items={[
            "cifratura del traffico in transito (HTTPS/TLS su tutti i domini e sottodomini);",
            "password degli utenti conservate solo come hash, mai in chiaro;",
            "token di sessione in cookie HttpOnly e Secure, con durata limitata;",
            "separazione logica dei dati di ciascun centro (architettura multi-tenant);",
            "accesso ai sistemi di produzione limitato al personale autorizzato;",
            "backup periodici del database.",
          ]}
        />
        <p>
          In caso di violazione dei dati personali che comporti un rischio per i diritti e
          le libertà delle persone, notificheremo il Garante entro 72 ore ai sensi
          dell'art. 33 GDPR e informeremo gli interessati quando il rischio è elevato.
        </p>
      </Section>

      <Section n={9} title="I tuoi diritti">
        <p>In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 GDPR:</p>
        <List
          items={[
            "accesso ai tuoi dati e ottenimento di una copia;",
            "rettifica dei dati inesatti e integrazione di quelli incompleti;",
            "cancellazione (“diritto all'oblio”) nei casi previsti;",
            "limitazione del trattamento;",
            "portabilità, in formato strutturato e leggibile da dispositivo automatico;",
            "opposizione al trattamento fondato sul legittimo interesse;",
            "revoca del consenso in ogni momento, senza pregiudicare la liceità del trattamento già effettuato.",
          ]}
        />
        <p>
          Scrivi a{" "}
          <a href={`mailto:${privacyContact()}`} className="underline underline-offset-4">
            {privacyContact()}
          </a>
          : rispondiamo entro un mese. Se ritieni che il trattamento violi il Regolamento
          puoi proporre reclamo al{" "}
          <a
            href="https://www.garanteprivacy.it"
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-4"
          >
            Garante per la protezione dei dati personali
          </a>{" "}
          (Piazza Venezia 11, 00187 Roma) o ricorso all'autorità giudiziaria.
        </p>
      </Section>

      <Section n={10} title="Conferimento dei dati">
        <p>
          Il conferimento dei dati contrassegnati come obbligatori nei moduli è necessario
          per dare seguito alla richiesta: senza di essi non possiamo rispondere o attivare
          il servizio. Tutti gli altri dati sono facoltativi.
        </p>
      </Section>

      <Section n={11} title="Modifiche">
        <p>
          Possiamo aggiornare questa informativa per adeguarla a nuove funzionalità o a
          modifiche normative. La versione vigente è sempre pubblicata a questo indirizzo,
          con data di ultimo aggiornamento in testa. In caso di modifiche sostanziali ti
          avviseremo per email o con un avviso ben visibile sul sito.
        </p>
      </Section>
    </LegalLayout>
  );
}
